/* eslint-disable react-hooks/exhaustive-deps */
import localforage from "localforage";
import React from "react";
import { isMockNegotiation, RTA } from "../../services/negotiationBridge";
import { sessionInfo } from "../../utils/utils";

const emptyPermissions = {
  orgInfos: [],
  orgCollaborators: [],
  orgRoles: [],
  orgPayments: [],
  invoices: [],
  dashboard: [],
  support: [],
  media: [],
  auditLog: [],
  contact: [],
  hall: [],
  roomRadioAds: [],
  roomTvAds: [],
  roomChatAds: [],
  hallAds: [],
  bid: [],
  handleNegotiateConfigs: [],
  report: [],
};

const PermissionsContext = React.createContext<{
  permissions: RolePermissions;
  setPermissions: React.Dispatch<React.SetStateAction<RolePermissions>>;
  permissionsLoaded: boolean;
  setPermissionsLoaded: React.Dispatch<React.SetStateAction<boolean>>;
}>({
  permissions: emptyPermissions,
  setPermissions: () => {},
  permissionsLoaded: false,
  setPermissionsLoaded: () => {},
});

export type possibleValues =
  | "view"
  | "create"
  | "edit"
  | "delete"
  | "grant"
  | "revoke"
  | "setBudget"
  | "viewRanking"
  | "viewHistory"
  | "export"
  | "upload"
  | "all";

export type RolePermissions = {
  orgInfos: string[];
  orgCollaborators: string[];
  orgRoles: string[];
  orgPayments: string[];
  invoices: string[];
  dashboard: string[];
  support: string[];
  media: string[];
  auditLog: string[];
  contact: string[];
  hall: string[];
  roomRadioAds: string[];
  roomTvAds: string[];
  roomChatAds: string[];
  hallAds: string[];
  bid: string[];
  handleNegotiateConfigs: string[];
  report: string[];
};

export const PermissionsProvider: React.FC<{ children: any }> = ({
  children,
}) => {
  const [permissions, setPermissions] =
    React.useState<RolePermissions>(emptyPermissions);
  const [permissionsLoaded, setPermissionsLoaded] = React.useState(false);

  class SocketEvents {
    static updatePermissions(payload: RolePermissions) {
      localforage.setItem("permissions", payload);
      setPermissions(payload);
    }
  }

  React.useEffect(() => {
    localforage
      .getItem("permissions")
      .then((permissions) => {
        if (permissions) setPermissions(permissions as RolePermissions);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setPermissionsLoaded(true);
      });
  }, []);

  React.useEffect(() => {
    sessionInfo().then((info) => {
      if (!info) return;
      RTA.on(
        `permissionsUpdate-${info.org_id}-${info.iss}`,
        SocketEvents.updatePermissions,
      );
    });

    return () => {
      sessionInfo().then((info) => {
        if (!info) return;
        RTA.off(
          `permissionsUpdate-${info.org_id}-${info.iss}`,
          SocketEvents.updatePermissions,
        );
      });
    };
  }, []);

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        setPermissions,
        permissionsLoaded,
        setPermissionsLoaded,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  return React.useContext(PermissionsContext);
};

export const checkPermission = (
  permission: string[],
  action: possibleValues,
) => {
  if (isMockNegotiation()) return true;
  if (!permission) return false;
  const permissionToLowerCase = permission.map((p) => p.toLowerCase());
  return (
    permissionToLowerCase.includes(action.toLowerCase()) ||
    permissionToLowerCase.includes("all")
  );
};
